<script setup lang="ts">
/**
 * An imported page: its background, its text exactly where it was, and the
 * frames somebody rewrote flowing in their boxes.
 *
 * The page is laid out in the PDF's own points and scaled to the sheet, so a
 * report on the same paper shows it at exactly its size and one on other
 * paper shows it whole. It fills its sheet edge to edge, the way a full-page
 * picture does, and keeps a sheet to itself (`domain/sheets.ts`).
 *
 * On the canvas, every frame has an invisible handle over it: a click picks
 * it for the inspector, a double click rewrites it in place. The handles are
 * chrome, and neither print nor export draws them.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import type { ImportedFont } from '@/domain/facsimile/fonts'
import { currentRuns, rewrite } from '@/domain/facsimile/text'
import type { EditedRun, FacsimileFrame } from '@/domain/facsimile/types'
import { parseMeasure } from '@/domain/layout/measure'
import type { FixedPageSection } from '@/domain/section'
import FacsimileText from '@/sections/fixedPage/FacsimileText.vue'
import FlowingFrame from '@/sections/fixedPage/FlowingFrame.vue'
import { useFrameSelection } from '@/sections/fixedPage/frameSelection'
import { useReportStore } from '@/stores/report'

const POINTS_PER_MM = 72 / 25.4

const { t } = useI18n()

const props = defineProps<{ section: FixedPageSection }>()

const report = useReportStore()
const selection = useFrameSelection()

const fonts = computed<ReadonlyMap<string, ImportedFont>>(
  () => new Map(report.fonts.map((font) => [font.key, font])),
)

/** How much the page in points has to grow or shrink to fill the sheet's width. */
const scale = computed(() => {
  const sheet = (parseMeasure(report.layout.pageWidth) ?? 210) * POINTS_PER_MM
  return props.section.width > 0 ? sheet / props.section.width : 1
})

const editing = computed(() =>
  props.section.frames.find((frame) => selection.isEditing(props.section.id, frame.id)),
)

const untouched = computed(() =>
  props.section.frames.filter((frame) => !frame.edited && frame !== editing.value),
)

const rewritten = computed(() =>
  props.section.frames.filter((frame) => frame.edited && frame !== editing.value),
)

function boxStyle(frame: FacsimileFrame): Record<string, string> {
  const [left, top, right, bottom] = frame.box
  return {
    left: `${left}pt`,
    top: `${top}pt`,
    width: `${right - left}pt`,
    height: `${bottom - top}pt`,
    // A turned frame's box is given unturned: turned about its middle, it covers its words.
    ...(frame.rotation ? { transform: `rotate(${frame.rotation}deg)` } : {}),
  }
}

function choose(frame: FacsimileFrame): void {
  report.select(props.section.id)
  selection.choose(props.section.id, frame.id)
}

function edit(frame: FacsimileFrame): void {
  if (!report.editable) return
  report.select(props.section.id)
  selection.edit(props.section.id, frame.id)
}

function commit(frame: FacsimileFrame, runs: EditedRun[]): void {
  selection.stopEditing()
  const next = rewrite(frame, runs)
  if (next.edited === frame.edited) return
  report.update({
    ...props.section,
    frames: props.section.frames.map((one) => (one.id === frame.id ? next : one)),
  })
}
</script>

<template>
  <div class="fixed-page">
    <div
      class="fixed-page__sheet"
      :style="{
        width: `${section.width}pt`,
        height: `${section.height}pt`,
        transform: scale === 1 ? undefined : `scale(${scale})`,
      }"
    >
      <img
        v-if="section.background"
        class="fixed-page__background"
        :src="section.background"
        alt=""
      />

      <FacsimileText
        :frames="untouched"
        :width="section.width"
        :height="section.height"
        :fonts="fonts"
      />

      <FlowingFrame
        v-for="frame in rewritten"
        :key="frame.id"
        :frame="frame"
        :runs="frame.edited ?? []"
        :fonts="fonts"
        @overflow="selection.setOverflow(section.id, frame.id, $event)"
      />

      <!-- Text that runs past its box, marked the way layout programs mark it. -->
      <template v-for="frame in rewritten" :key="`overset-${frame.id}`">
        <span
          v-if="selection.overflowOf(section.id, frame.id) > 0"
          class="fixed-page__overset"
          :style="{ left: `${frame.box[2] - 5}pt`, top: `${frame.box[3] - 5}pt` }"
          :title="t('inspector.fixedPage.oversetHint')"
          aria-hidden="true"
          >+</span
        >
      </template>

      <FlowingFrame
        v-if="editing"
        :key="`editing-${editing.id}`"
        :frame="editing"
        :runs="currentRuns(editing)"
        :fonts="fonts"
        editing
        @commit="commit(editing, $event)"
        @cancel="selection.stopEditing()"
      />

      <template v-for="frame in section.frames" :key="frame.id">
        <div
          v-if="frame !== editing"
          class="fixed-page__handle"
          :class="{
            'fixed-page__handle--chosen': selection.isChosen(section.id, frame.id),
            'fixed-page__handle--outlined': selection.outlined.value,
            'fixed-page__handle--edited': frame.edited !== null,
          }"
          :style="boxStyle(frame)"
          :title="report.editable ? t('inspector.fixedPage.handleHint') : undefined"
          aria-hidden="true"
          @click.stop="choose(frame)"
          @dblclick.stop="edit(frame)"
        />
      </template>

      <p v-if="section.frames.length === 0 && !section.background" class="fixed-page__empty">
        {{ t('inspector.fixedPage.empty') }}
      </p>
    </div>
  </div>
</template>

<style scoped>
/* The whole sheet, edge to edge: pulled out over all four margins. The foot
   is safe to pull, because the page always ends its sheet. */
.fixed-page {
  position: relative;
  height: var(--page-height, 297mm);
  margin: calc(var(--page-margin-top, 22mm) * -1) calc(var(--page-margin-side, 20mm) * -1)
    calc(var(--page-margin-bottom, 24mm) * -1);
  overflow: hidden;
  background: #fff;
}

.fixed-page__sheet {
  position: relative;
  transform-origin: top left;
}

.fixed-page__background {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  user-select: none;
  pointer-events: none;
}

.fixed-page__handle {
  position: absolute;
  z-index: 2;
  border-radius: 2px;
  cursor: pointer;
  transition: box-shadow var(--quick, 110ms);
}

.fixed-page__handle:hover,
.fixed-page__handle--outlined {
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--accent-fill) 55%, transparent);
}

.fixed-page__handle--edited.fixed-page__handle--outlined {
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--warn) 80%, transparent);
}

.fixed-page__handle--chosen {
  box-shadow: 0 0 0 1.5px var(--accent-fill);
}

.fixed-page__overset {
  position: absolute;
  z-index: 3;
  display: grid;
  place-items: center;
  width: 10pt;
  height: 10pt;
  background: #e5484d;
  color: #fff;
  font: 700 9pt/1 var(--font-ui, sans-serif);
  pointer-events: none;
}

.fixed-page__empty {
  position: absolute;
  inset: 40% 0 auto;
  margin: 0;
  color: var(--paper-ink-soft);
  font-family: var(--font-ui, sans-serif);
  font-size: 9pt;
  text-align: center;
}

@media print {
  .fixed-page {
    break-before: page;
    break-after: page;
  }

  .fixed-page__handle,
  .fixed-page__overset,
  .fixed-page__empty {
    display: none;
  }
}
</style>
